import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Text,
  Button,
  TextInput,
  Form,
  ThemeContextExtend,
  Captcha,
  Loader,
} from '@usap/uicomponents'
import validate from '../../../helpers/validation'

import { Heading5 } from '../../common/heading'
import { policySchema } from '../../common/ValidationSchema'
import FormTheme from '../../common/config/form-theme'
import UserInfoMsg from '../../common/UserInfoMsg'
import {
  LowPriceAsterisk,
  LowPriceInnerBox,
  LowPriceOuterBox,
  LowPriceRequiredText,
  OuterFormBox,
  InfoHeadingContainer,
  ThemeLowPriceHeadingBox,
  ThemeLowPriceOuterBox,
  CaptchaContainer,
  CaptchaWrapper,
  CaptchaOuterBox,
  CaptchaInnerBox,
  SubmitButton,
} from './style/PolicyForm.css'
import { GTM } from '@usap/carparts/src/shared/helpers'

const defaultValues = {
  custName: '',
  custEmail: '',
  custPhoneNumber: '',
  custShippingAddress: '',
  partNumber: '',
  compName: '',
  compSku: '',
  compPrice: '',
  compShipping: '',
  compUrl: '',
  captcha: false,
}
const fieldSetStyle = {
  borderRadius: '4px',
  border: '1px solid #666666',
  height: '49px',
}
const textInputStyle = {
  padding: '0px',
  fontSize: '16px',
  fontFamily: 'Roboto',
}

const FormBox = ({
  title,
  id,
  name,
  type,
  value,
  onInputChangeHandler,
  error,
  ariaLabel,
}) => (
  <Box
    direction="column"
    align="center"
    justify="center"
  >
    <OuterFormBox>
      <Box width="607px">
        <fieldset style={fieldSetStyle}>
          <legend>
            <Text size="12px" color="#333333">
              {`${title} `}
              <Text size="12px" color="#ff9700">*</Text>
            </Text>
          </legend>
          <TextInput
            style={textInputStyle}
            id={id}
            name={name}
            aria-label={ariaLabel}
            type={type}
            plain
            value={value}
            onChange={event => {
              onInputChangeHandler(name, event.target.value)
            }}
          />
        </fieldset>
      </Box>
    </OuterFormBox>
    <Box pad="8px" alignSelf="start">
      <Text color="#f44635" size="xsmall">
        {error}
      </Text>
    </Box>
  </Box>
)

export default function PolicyForm({
  formSubmit,
  icon,
  message,
  setMessage,
  siteKey,
  isLoading,
  setLoader,
}) {
  const [errors, setError] = useState({})
  const [formData, setFormData] = useState(defaultValues)

  const [captcha, setCaptcha] = useState(false)
  const isFormFieldClicked = useRef(null)
  const { customFormStart, customGenerateLead } = GTM

  const onInputChangeHandler = (name, value) => {
    setMessage('')
    setError({ ...errors, [name]: '' })
    setFormData({ ...formData, [name]: value })
    if(!isFormFieldClicked.current) {
      isFormFieldClicked.current = true
      customFormStart("Policy form", "policyForm")
    }
  }

  const onFormSubmit = () => {
    setMessage('')
    const hasError = validate(formData, policySchema(formData))
    if (hasError) {
      setLoader(false)
      return setError(hasError)
    }

    setLoader(false)
    document.getElementById('policyForm').reset()
    customGenerateLead("Policy form", "policyForm")
    isFormFieldClicked.current = false
    return formSubmit(formData).then(setFormData)
  }

  useEffect(() => {
    if (captcha) {
      setMessage('')
      setError({ ...errors, captcha: false })
      setFormData({ ...formData, captcha })
    }
  }, [captcha])

  return (
    <Box id="lowPricePolicyForm" width="100%">
      {isLoading && <Loader />}
      {message && (
        <UserInfoMsg
          bgColor={icon === 'help' ? 'status-warning' : 'green'}
          icon={icon}
          infoType={message}
        />
      )}
      <LowPriceOuterBox>
        <Heading5 size="20px" title="Price Match Request Form" />
        <LowPriceInnerBox>
          <LowPriceRequiredText>
            <LowPriceAsterisk>* </LowPriceAsterisk>
            Required fields
          </LowPriceRequiredText>
        </LowPriceInnerBox>
      </LowPriceOuterBox>
      <ThemeContextExtend value={FormTheme}>
        <Form id="policyForm" onSubmit={onFormSubmit}>
          <ThemeLowPriceOuterBox>
            <ThemeLowPriceHeadingBox>
              <Heading5 size="small" title="Your Contact Info" />
            </ThemeLowPriceHeadingBox>

            <Box alignSelf="start">
              <FormBox
                title="Name"
                id="custName"
                name="custName"
                ariaLabel="customer name"
                type="text"
                value={formData.custName}
                onInputChangeHandler={onInputChangeHandler}
                error={errors.custName}
              />

              <FormBox
                title="Email Address"
                id="custEmail"
                name="custEmail"
                ariaLabel="customer email address"
                type="email"
                value={formData.custEmail}
                onInputChangeHandler={onInputChangeHandler}
                error={errors.custEmail}
              />

              <FormBox
                title="Phone Number"
                id="custPhoneNumber"
                name="custPhoneNumber"
                ariaLabel="customer phone number"
                type="number"
                value={formData.custPhoneNumber}
                onInputChangeHandler={onInputChangeHandler}
                error={errors.custPhoneNumber}
              />

              <FormBox
                title="Shipping Address"
                id="custShippingAddress"
                name="custShippingAddress"
                ariaLabel="customer shipping address"
                type="text"
                value={formData.custShippingAddress}
                onInputChangeHandler={onInputChangeHandler}
                error={errors.custShippingAddress}
              />
            </Box>
          </ThemeLowPriceOuterBox>

          <ThemeLowPriceOuterBox>
            <InfoHeadingContainer>
              <Heading5 size="small" title="Parts Info" />
            </InfoHeadingContainer>
            <Box alignSelf="start">
              <FormBox
                title="CarParts.com Part Number"
                id="partNumber"
                name="partNumber"
                ariaLabel="part number"
                type="text"
                value={formData.partNumber}
                onInputChangeHandler={onInputChangeHandler}
                error={errors.partNumber}
              />
            </Box>
          </ThemeLowPriceOuterBox>

          <ThemeLowPriceOuterBox>
            <InfoHeadingContainer>
              <Heading5 size="small" title="Competitor Info" />
            </InfoHeadingContainer>
            <Box alignSelf="start">
              <FormBox
                title="Competitor Name"
                id="compName"
                name="compName"
                ariaLabel="competitor name"
                type="text"
                value={formData.compName}
                onInputChangeHandler={onInputChangeHandler}
                error={errors.compName}
              />

              <FormBox
                title="Competitor Part Number"
                id="compSku"
                name="compSku"
                ariaLabel="competitor part number"
                type="text"
                value={formData.compSku}
                onInputChangeHandler={onInputChangeHandler}
                error={errors.compSku}
              />

              <FormBox
                title="Competitor Item Price"
                id="compPrice"
                name="compPrice"
                ariaLabel="competitor item price"
                type="text"
                value={formData.compPrice}
                onInputChangeHandler={onInputChangeHandler}
                error={errors.compPrice}
              />

              <FormBox
                title="Competitor Shipping Charge"
                id="compShipping"
                name="compShipping"
                ariaLabel="competitor shipping charge"
                type="text"
                value={formData.compShipping}
                onInputChangeHandler={onInputChangeHandler}
                error={errors.compShipping}
              />

              <FormBox
                title="Competitor Listing URL"
                id="compUrl"
                name="compUrl"
                ariaLabel="competitor listing url"
                type="text"
                value={formData.compUrl}
                onInputChangeHandler={onInputChangeHandler}
                error={errors.compUrl}
              />
            </Box>
          </ThemeLowPriceOuterBox>

          <CaptchaContainer>
            <CaptchaWrapper>
              <CaptchaOuterBox>
                <CaptchaInnerBox>
                  <Captcha
                    id="policyform"
                    name="policyform"
                    setCaptcha={setCaptcha}
                    error={errors.captcha}
                    siteKey={siteKey}
                    reset={Boolean(message)}
                  />
                </CaptchaInnerBox>
              </CaptchaOuterBox>
              <Box width="100%" alignSelf="start">
                <SubmitButton
                  id="submitPolicy"
                  form="policyForm"
                  primary
                  type="submit"
                  custom
                  hideBorder
                  label="Submit"
                />
              </Box>
            </CaptchaWrapper>
          </CaptchaContainer>
        </Form>
      </ThemeContextExtend>
    </Box>
  )
}
